import * as i18n from '../i18n.js';

/**
 * The lardr emblem: a freshness-gauge ring (faint track + cyan progress arc)
 * around a produce cluster — a green apple with a leaf sprig, in front of an
 * orange and a berry.
 *
 * Inlined rather than an `<img>` so the ring track inherits `currentColor` —
 * white on the gradient header, the app's ink colour anywhere else. The cyan
 * arc and the produce keep fixed brand colours on every background. The
 * viewBox is cropped to the ring's stroke box, so there is no dead padding to
 * compensate for at call sites.
 *
 * geometry-exempt: this is the product's own mark, not an icon. No vendored set
 * can supply it, and replacing it is a branding decision rather than a drawing
 * one. It is the single piece of bespoke artwork the app still carries.
 *
 * @param {string} [sizeClasses] Tailwind sizing for the <svg>.
 * @returns {HTMLSpanElement}
 */
export function brandEmblem(sizeClasses = 'h-10 w-10') {
  const span = document.createElement('span');
  span.className = 'shrink-0';
  span.setAttribute('aria-hidden', 'true');
  span.innerHTML = `<svg viewBox="2.5 3.5 59 59" fill="none" class="${sizeClasses} block">
    <defs>
      <linearGradient id="brand-apple" x1="32" y1="23" x2="32" y2="42" gradientUnits="userSpaceOnUse">
        <stop offset="0" stop-color="#97d957"/>
        <stop offset="1" stop-color="#55a22b"/>
      </linearGradient>
    </defs>
    <circle cx="32" cy="33" r="27" stroke="currentColor" stroke-opacity="0.30" stroke-width="4.5"/>
    <path d="M27.3 6.4 A27 27 0 0 1 52.7 50.4" stroke="#2dd4e1" stroke-width="4.5" stroke-linecap="round"/>
    <circle cx="23" cy="41" r="7.5" fill="#ed7a2e"/>
    <circle cx="41" cy="41.5" r="6.75" fill="#de3b4f"/>
    <circle cx="20.4" cy="38.2" r="1.2" fill="#ffffff" opacity="0.3"/>
    <circle cx="38.7" cy="39.2" r="1" fill="#ffffff" opacity="0.28"/>
    <path d="M 32 25.4 C 29.5 23.4 24.5 24.1 22.75 27.9 C 21 31.6 22 37.6 25 40.6 C 27 42.6 30 43.4 32 42.1 C 34 43.4 37 42.6 39 40.6 C 42 37.6 43 31.6 41.25 27.9 C 39.5 24.1 34.5 23.4 32 25.4 Z" fill="url(#brand-apple)"/>
    <ellipse cx="27.6" cy="31" rx="1.9" ry="3.4" transform="rotate(-22 27.6 31)" fill="#ffffff" opacity="0.32"/>
    <path d="M 32.25 25.1 C 32 23.1 32.25 21.6 33.5 20.3" stroke="#6b4423" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M 33.75 21 C 35.25 17 39.75 15.25 43.5 16.25 C 42.75 20.25 38.75 23 34.25 22.25 C 34 21.8 33.85 21.4 33.75 21 Z" fill="#6fb43a"/>
  </svg>`;
  return span;
}

/**
 * Brand lockup as a home link. Used twice: at the head of the desktop sidebar
 * and — below `lg`, where there is no sidebar — in the header itself.
 *
 * @param {{ emblemClasses?: string, wordClasses?: string, tagline?: boolean }} [opts]
 * @returns {HTMLAnchorElement}
 */
export function brandLockup({
  emblemClasses = 'h-10 w-10',
  wordClasses = 'font-bold text-xl tracking-tight',
  tagline = false,
} = {}) {
  const link = document.createElement('a');
  link.href = '#/';
  link.dataset.role = 'brand-lockup';
  link.className = 'flex items-center gap-2.5 shrink-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary';
  link.setAttribute('aria-label', `lardr — ${i18n.t('app.title')}`);

  link.append(brandEmblem(emblemClasses));

  const stack = document.createElement('span');
  stack.className = 'flex flex-col leading-none min-w-0';

  const word = document.createElement('span');
  word.className = wordClasses;
  word.textContent = 'lardr';
  stack.append(word);

  if (tagline) {
    const sub = document.createElement('span');
    sub.className = 'text-xs opacity-70 mt-0.5 truncate';
    sub.textContent = i18n.t('app.tagline');
    stack.append(sub);
  }

  link.append(stack);
  return link;
}
